// The two byte-level operations a capture needs: identify a photograph, and
// shrink it.
//
// ---------------------------------------------------------------------------
// THIS IS A DELIBERATE COPY OF TWO FUNCTIONS FROM apps/nricher/app/lib/scanPhoto.js
// ---------------------------------------------------------------------------
// That module is Quick Scan's, and it carries six more exports about file
// pickers and upload limits that this app has no picker and no limits for. The
// alternative to copying was a shared workspace package for ~80 lines, which
// buys a build-order dependency between two apps that otherwise share nothing —
// and this app exists precisely so that it shares nothing.
//
// ⚠️ BOTH FUNCTIONS ARE LOAD-BEARING IN WAYS THAT ARE NOT OBVIOUS, and the
// reasons are recorded below. If a bug is fixed in one copy, fix the other:
// `hashBlob` decides an S3 key, and `downscale` decides whether a photograph
// reaches the model the right way up.
//
// The live camera no longer uses `downscale`: it encodes once from a canvas
// already sized at SHOT_MAX_EDGE.

/** Longest edge kept, matching scanPhoto's MAX_EDGE. */
const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.85;

/**
 * A stable content identity for a photograph, used as part of its S3 key.
 *
 * CONTENT-ADDRESSED, so that re-uploading the same photograph writes identical
 * bytes to the identical key — which is what makes a retry idempotent with no
 * dedup logic anywhere in the system.
 *
 * `crypto.subtle` needs a secure context. This host is https (getUserMedia
 * would not work otherwise), so the fallback is close to unreachable — but it
 * keeps a non-secure context working, and the hash is persisted on the record
 * the moment it is computed, so even a random one is stable across every retry
 * of that capture. It stops being content-addressed; it does not stop being
 * deterministic.
 */
export async function hashBlob(blob) {
  try {
    const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);
  } catch {
    return `nohash${Math.random().toString(16).slice(2, 12)}${Date.now().toString(16)}`.slice(
      0,
      32,
    );
  }
}

/**
 * Downscale and re-encode one photograph.
 *
 * ⚠️ ORIENTATION IS THE TRAP. Phone cameras record rotation as an EXIF tag and
 * leave the pixels as the sensor saw them. Re-encoding through a canvas writes
 * a file with NO EXIF, so if the rotation is not baked into the pixels here it
 * is lost for good — and `packshot.service.js`'s `sharp().rotate()`, which
 * exists to fix exactly this, would find nothing to act on and every photograph
 * from a portrait-held phone would reach the model sideways. That was a real
 * production bug (#80). `imageOrientation: "from-image"` is what applies it.
 *
 * Returns the ORIGINAL blob unchanged if anything at all goes wrong. A capture
 * that uploads 2.8MB is slower; a capture that throws here is lost, and the
 * person is standing in front of a shelf.
 */
export async function downscale(blob, opts = {}) {
  const maxEdge = opts.maxEdge || MAX_EDGE;
  const quality = opts.quality || JPEG_QUALITY;
  try {
    if (typeof createImageBitmap !== "function") return blob;

    const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    const { width, height } = bitmap;
    const longest = Math.max(width, height);

    // Already small enough. Re-encoding would cost quality for nothing.
    if (longest <= maxEdge) {
      bitmap.close?.();
      return blob;
    }

    const scale = maxEdge / longest;
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return blob;
    }
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close?.();

    const encoded = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );

    // A canvas that produced nothing, or somehow produced something LARGER
    // than the original — keep what we had rather than make the upload worse.
    if (!encoded || encoded.size === 0 || encoded.size >= blob.size) return blob;
    return encoded;
  } catch {
    return blob;
  }
}
